import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/9e8b8adb-4702-43d4-a386-2eaf3df07374/images/bb5f3c6757d7d042a11246056b3090524e1c5974db1b3942804fa38cf37ab5b9.png";

const sections = [
  {
    title: '1. Information We Collect',
    content: `When you use our website or book our services, we may collect the following personal information: your full name, email address, phone number, pickup and drop-off locations, travel dates and times, vehicle and occasion preferences, and any special requests or notes you provide. We also automatically collect certain technical information, including your IP address, browser type, device information, and pages visited on our website through cookies and similar technologies.`,
  },
  {
    title: '2. How We Use Your Information',
    content: `We use your personal information to process and confirm your bookings, provide our chauffeur and transportation services, communicate with you regarding your reservations (via email, SMS, or phone), send booking confirmations and service updates, improve our website and services, comply with legal obligations, and protect our rights and the safety of our clients and staff. We do not use your information for unsolicited marketing purposes without your explicit consent.`,
  },
  {
    title: '3. How We Share Your Information',
    content: `We do not sell, trade, or rent your personal information to third parties. We may share your information with our chauffeurs to the extent necessary to provide the requested service, with payment processors to complete transactions, with service providers who assist us in operating our website and business (such as email and SMS notification services), and with law enforcement or regulatory authorities when required by law. All third-party service providers are contractually obligated to protect your information and use it only for the purposes we specify.`,
  },
  {
    title: '4. Data Security',
    content: `We take the security of your personal information seriously. We implement appropriate technical and organizational measures to protect your data against unauthorized access, alteration, disclosure, or destruction. These measures include encrypted data transmission (SSL/TLS), secure server infrastructure, restricted access to personal information, and regular security assessments. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security of your data.`,
  },
  {
    title: '5. Cookies & Tracking',
    content: `Our website uses cookies and similar tracking technologies to enhance your browsing experience. We use essential cookies required for the website to function properly, analytics cookies (Google Analytics) to understand how visitors interact with our website, and preference cookies to remember your settings and choices. You can control cookie preferences through your browser settings. Disabling certain cookies may affect the functionality of our website.`,
  },
  {
    title: '6. Data Retention',
    content: `We retain your personal information only for as long as necessary to fulfill the purposes for which it was collected, including to satisfy any legal, accounting, or reporting requirements. Booking records are retained for a period of 7 years for tax and legal compliance purposes. You may request deletion of your personal data at any time, subject to our legal retention obligations.`,
  },
  {
    title: '7. Your Rights',
    content: `Under applicable Canadian privacy legislation (including PIPEDA), you have the right to access the personal information we hold about you, request correction of inaccurate or incomplete information, request deletion of your personal information (subject to legal exceptions), withdraw consent for the processing of your data, and lodge a complaint with the Office of the Privacy Commissioner of Canada. To exercise any of these rights, please contact us using the information provided below.`,
  },
  {
    title: '8. Third-Party Links',
    content: `Our website may contain links to third-party websites or services that are not operated by Toronto Royale. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services. We encourage you to review the privacy policies of any third-party sites you visit.`,
  },
  {
    title: '9. Children\'s Privacy',
    content: `Our Services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you become aware that a child has provided us with personal information, please contact us immediately, and we will take steps to delete such information.`,
  },
  {
    title: '10. Changes to This Policy',
    content: `Toronto Royale reserves the right to update this Privacy Policy at any time. Changes will be posted on this page with an updated "Last updated" date. We encourage you to review this Privacy Policy periodically. Your continued use of our Services after any changes constitutes acceptance of the updated policy.`,
  },
  {
    title: '11. Contact Us',
    content: `If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at: bookings@torontoroyale.com or call +1 (437) 996-3801. Toronto Royale, Greater Toronto Area, Ontario, Canada.`,
  },
];

export const Privacy = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('fade-in'); }),
      { threshold: 0.05, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.fade-element').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div data-testid="privacy-page" style={{ backgroundColor: '#0A0A0A' }}>

      {/* ── HERO ── */}
      <section data-testid="privacy-hero" className="relative overflow-hidden flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${HERO_IMG})`,
          filter: 'brightness(0.30) saturate(0.7) contrast(1.08)',
          scale: '1.05',
          animation: 'heroZoom 25s ease-in-out infinite alternate',
        }} />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.7) 100%)',
        }} />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 40%, rgba(194,168,120,0.05) 0%, transparent 55%)',
        }} />
        <div className="grain-overlay absolute inset-0" style={{ opacity: 0.04 }} />

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto" style={{ paddingTop: '160px', paddingBottom: '120px' }}>
          <div className="fade-element" style={{ animationDelay: '0.2s' }}>
            <div className="gold-divider mx-auto" style={{ marginBottom: '28px', opacity: 0.7 }} />
            <p style={{ fontSize: '11px', color: 'rgba(194,168,120,0.55)', letterSpacing: '5px', textTransform: 'uppercase', fontWeight: 300, marginBottom: '32px' }}>
              Legal
            </p>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.4s' }}>
            <h1 data-testid="privacy-hero-heading" className="font-serif" style={{
              fontSize: 'clamp(36px, 5vw, 58px)', fontWeight: 500, color: '#FAFAFA',
              marginBottom: '24px', lineHeight: 1.1, textShadow: '0 2px 40px rgba(0,0,0,0.5)',
            }}>
              Privacy Policy
            </h1>
          </div>
          <div className="fade-element" style={{ animationDelay: '0.6s' }}>
            <p style={{ fontSize: '14px', color: 'rgba(220,220,220,0.5)', fontWeight: 300 }}>
              Last updated: February 2026
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0" style={{ height: '120px', background: 'linear-gradient(to top, #0A0A0A, transparent)' }} />
      </section>

      {/* ── CONTENT ── */}
      <section className="relative" style={{ backgroundColor: '#0A0A0A', padding: '4rem 1.5rem 10rem' }}>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 70% 20%, rgba(194,168,120,0.02) 0%, transparent 50%)',
        }} />

        <div className="max-w-3xl mx-auto relative z-10">

          {/* Intro */}
          <div className="fade-element" style={{ marginBottom: '56px' }}>
            <p style={{
              fontSize: '15px', color: 'rgba(204,204,204,0.55)', fontWeight: 300,
              lineHeight: 2, textAlign: 'center', maxWidth: '600px', margin: '0 auto',
            }}>
              Toronto Royale ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our chauffeur services.
            </p>
          </div>

          {sections.map((section, i) => (
            <div key={i} className="fade-element" style={{ marginBottom: '48px' }}>
              <h2 className="font-serif" style={{
                fontSize: '20px', fontWeight: 400, color: '#F5F5F5',
                marginBottom: '16px', lineHeight: 1.3,
              }}>
                {section.title}
              </h2>
              <p style={{
                fontSize: '14px', color: 'rgba(225,225,225,0.8)', fontWeight: 300,
                lineHeight: 2,
              }}>
                {section.content}
              </p>
              {i < sections.length - 1 && (
                <div style={{ marginTop: '48px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(194,168,120,0.06) 20%, rgba(194,168,120,0.06) 80%, transparent)' }} />
              )}
            </div>
          ))}

          <div className="fade-element" style={{ marginTop: '64px', textAlign: 'center' }}>
            <p style={{ fontSize: '14px', color: 'rgba(225,225,225,0.75)', fontWeight: 300 }}>
              See also our{' '}
              <Link to="/terms" style={{ color: '#C2A878', textDecoration: 'none', transition: 'opacity 300ms ease' }}
                onMouseEnter={(e) => { e.target.style.opacity = '0.7'; }}
                onMouseLeave={(e) => { e.target.style.opacity = '1'; }}
              >
                Terms & Conditions
              </Link>
            </p>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes heroZoom {
          0% { transform: scale(1.05); }
          100% { transform: scale(1.12); }
        }
      `}</style>
    </div>
  );
};
